import { useState, type FormEvent } from 'react';
import { Badge } from '@rudi/ui/badge';
import { Button } from '@rudi/ui/button';
import { Checkbox } from '@rudi/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@rudi/ui/dialog';
import { Input } from '@rudi/ui/input';
import { Label } from '@rudi/ui/label';
import { Switch } from '@rudi/ui/switch';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@rudi/ui/table';
import { toast } from 'sonner';
import {
  useAddFeedbackAutomationTarget,
  useDeleteFeedbackAutomationTarget,
  useFeedbackAutomation,
  useResetFeedbackAutomationSettings,
  useSaveFeedbackAutomationSettings,
  useSetActiveFeedbackAutomationTarget,
  useUpdateFeedbackAutomationTarget,
} from '../api/hooks';
import type { FeedbackAutomationConfig, FeedbackAutomationTarget } from '../api/types';
import { QueryState } from '../components/page';
import { readTargetLabel } from '../platform/target';
import {
  AUTOMATION_UNAVAILABLE,
  automationWriteErrorText,
  categoryChoices,
  categoryLabel,
  destinationText,
  isHttpsUrl,
  parseThreshold,
} from './feedback';
import { ConfirmDialog } from './practice-agents';

const NO_ENCRYPTION =
  "This Rudi can't encrypt secrets, so it can't store targets. Give it an encryption key first.";

/** The Automation tab: where confident tickets go and which ones, on the targeted Rudi. */
export function FeedbackAutomationPanel() {
  const config = useFeedbackAutomation();
  const rudi = readTargetLabel();
  return (
    <QueryState isLoading={config.isLoading} error={config.error}>
      {config.data === null ? (
        <p role="status" className="py-12 text-center text-sm text-muted-foreground">
          {AUTOMATION_UNAVAILABLE}
        </p>
      ) : config.data ? (
        <div className="space-y-6">
          <p className="text-sm text-muted-foreground">
            Each Rudi keeps its own settings. This tab changes{' '}
            {rudi ? <span className="font-medium text-foreground">{rudi}</span> : 'this Rudi'}.
          </p>
          <TargetsSection config={config.data} />
          <SettingsForm
            key={`${config.data.revision}-${config.data.settingsSource}`}
            config={config.data}
          />
        </div>
      ) : null}
    </QueryState>
  );
}

type Switching = { kind: 'target'; target: FeedbackAutomationTarget } | { kind: 'environment' };

function switchDescription(config: FeedbackAutomationConfig, switching: Switching): string {
  const after = 'fixes already running keep reporting back.';
  if (switching.kind === 'target') {
    return `Tickets from now on go to ${switching.target.name} (${switching.target.url}); ${after}`;
  }
  if (!config.environmentHost) {
    return `The environment sets no Code webhook, so tickets from now on go nowhere; ${after}`;
  }
  return `Tickets from now on go to the environment fallback (${config.environmentHost}); ${after}`;
}

function TargetsSection({ config }: { config: FeedbackAutomationConfig }) {
  const setActive = useSetActiveFeedbackAutomationTarget();
  const remove = useDeleteFeedbackAutomationTarget();
  const [editing, setEditing] = useState<FeedbackAutomationTarget | 'new' | null>(null);
  const [switching, setSwitching] = useState<Switching | null>(null);
  const [deleting, setDeleting] = useState<FeedbackAutomationTarget | null>(null);
  const environmentActive = config.source === 'environment';
  const busy = setActive.isPending || remove.isPending;

  const confirmSwitch = async () => {
    if (!switching) return;
    try {
      await setActive.mutateAsync(switching.kind === 'target' ? switching.target.id : null);
      toast.success(
        switching.kind === 'target'
          ? `Tickets now go to ${switching.target.name}`
          : 'Tickets now use the environment fallback'
      );
      setSwitching(null);
    } catch (error) {
      toast.error(automationWriteErrorText(error, 'active'));
    }
  };

  const confirmDelete = async () => {
    if (!deleting) return;
    try {
      await remove.mutateAsync(deleting.id);
      toast.success(`${deleting.name} deleted`);
      setDeleting(null);
    } catch (error) {
      toast.error(automationWriteErrorText(error, 'delete'));
    }
  };

  return (
    <section aria-label="Where tickets go" className="space-y-3">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="text-sm font-medium">Where tickets go</h2>
          <p className="mt-1 text-xs text-muted-foreground">
            Rudi sends confident tickets to the active Code webhook, which opens a fix pull request.
            With no target active, the environment&apos;s webhook from OpenBao applies.
          </p>
        </div>
        <Button
          size="sm"
          disabled={!config.encryptionAvailable}
          title={config.encryptionAvailable ? undefined : NO_ENCRYPTION}
          onClick={() => setEditing('new')}
        >
          Add target
        </Button>
      </div>
      {!config.encryptionAvailable && (
        <p role="status" className="text-xs text-muted-foreground">
          {NO_ENCRYPTION}
        </p>
      )}
      {config.source === 'none' && (
        <p role="status" className="text-sm text-destructive">
          Tickets aren&apos;t sent anywhere: no target is active and the environment sets no Code
          webhook.
        </p>
      )}
      <div className="overflow-hidden rounded-3xl border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>URL</TableHead>
              <TableHead>Secret</TableHead>
              <TableHead>Status</TableHead>
              <TableHead />
            </TableRow>
          </TableHeader>
          <TableBody>
            {config.targets.map(target => (
              <TableRow key={target.id}>
                <TableCell className="font-medium">{target.name}</TableCell>
                <TableCell className="break-all font-mono text-xs">{target.url}</TableCell>
                <TableCell className="font-mono text-xs text-muted-foreground">
                  {target.secretHint}
                </TableCell>
                <TableCell>{target.active && <Badge>Active</Badge>}</TableCell>
                <TableCell>
                  <div className="flex justify-end gap-1">
                    {!target.active && (
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={busy}
                        onClick={() => setSwitching({ kind: 'target', target })}
                      >
                        Make active
                      </Button>
                    )}
                    <Button variant="ghost" size="sm" onClick={() => setEditing(target)}>
                      Edit
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      disabled={busy || target.active}
                      title={target.active ? 'Make another target active first' : undefined}
                      onClick={() => setDeleting(target)}
                    >
                      Delete
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
            <TableRow>
              <TableCell className="font-medium">Environment fallback</TableCell>
              <TableCell className="break-all font-mono text-xs">
                {config.environmentHost ?? (
                  <span className="font-sans text-muted-foreground">None set</span>
                )}
              </TableCell>
              <TableCell className="text-xs text-muted-foreground">From OpenBao</TableCell>
              <TableCell>{environmentActive && <Badge>Active</Badge>}</TableCell>
              <TableCell>
                <div className="flex justify-end">
                  {config.source === 'cortex' && (
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={busy}
                      onClick={() => setSwitching({ kind: 'environment' })}
                    >
                      Use environment fallback
                    </Button>
                  )}
                </div>
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </div>
      {editing && (
        <TargetDialog
          target={editing === 'new' ? null : editing}
          encryptionAvailable={config.encryptionAvailable}
          onClose={() => setEditing(null)}
        />
      )}
      <ConfirmDialog
        open={switching !== null}
        title={
          switching?.kind === 'target'
            ? `Send tickets to ${switching.target.name}`
            : 'Use the environment fallback'
        }
        description={switching ? switchDescription(config, switching) : ''}
        confirmLabel={setActive.isPending ? 'Switching…' : 'Switch'}
        pending={setActive.isPending}
        onConfirm={() => void confirmSwitch()}
        onClose={() => setSwitching(null)}
      />
      <ConfirmDialog
        open={deleting !== null}
        title={`Delete ${deleting?.name ?? 'target'}`}
        description="Rudi forgets this webhook's URL and secret."
        confirmLabel={remove.isPending ? 'Deleting…' : 'Delete'}
        pending={remove.isPending}
        destructive
        onConfirm={() => void confirmDelete()}
        onClose={() => setDeleting(null)}
      />
    </section>
  );
}

function TargetDialog({
  target,
  encryptionAvailable,
  onClose,
}: {
  target: FeedbackAutomationTarget | null;
  encryptionAvailable: boolean;
  onClose: () => void;
}) {
  const add = useAddFeedbackAutomationTarget();
  const update = useUpdateFeedbackAutomationTarget();
  const [name, setName] = useState(target?.name ?? '');
  const [url, setUrl] = useState(target?.url ?? '');
  const [secret, setSecret] = useState('');
  const pending = add.isPending || update.isPending;
  const urlInvalid = url.trim() !== '' && !isHttpsUrl(url);
  const edited = target
    ? name.trim() !== target.name || url.trim() !== target.url || !!secret.trim()
    : !!secret.trim() && encryptionAvailable;
  const ready = !!name.trim() && isHttpsUrl(url) && edited;

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (!ready || pending) return;
    try {
      if (target) {
        const patch = {
          ...(name.trim() !== target.name ? { name: name.trim() } : {}),
          ...(url.trim() !== target.url ? { url: url.trim() } : {}),
          ...(secret.trim() ? { secret: secret.trim() } : {}),
        };
        await update.mutateAsync({ targetId: target.id, patch });
        toast.success(`${name.trim()} saved`);
      } else {
        await add.mutateAsync({ name: name.trim(), url: url.trim(), secret: secret.trim() });
        toast.success(`${name.trim()} added`);
      }
      onClose();
    } catch (error) {
      toast.error(automationWriteErrorText(error, target ? 'edit' : 'add'));
    } finally {
      // The secret leaves the browser once and is never shown again.
      setSecret('');
    }
  };

  return (
    <Dialog open onOpenChange={open => !open && !pending && onClose()}>
      <DialogContent>
        <form onSubmit={event => void submit(event)} className="space-y-4">
          <DialogHeader>
            <DialogTitle>{target ? `Edit ${target.name}` : 'Add target'}</DialogTitle>
            <DialogDescription>
              A Code webhook that receives confident tickets and opens a fix pull request.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label htmlFor="automation-target-name">Name</Label>
            <Input
              id="automation-target-name"
              value={name}
              maxLength={100}
              placeholder="Code dev"
              onChange={event => setName(event.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="automation-target-url">URL</Label>
            <Input
              id="automation-target-url"
              type="url"
              value={url}
              placeholder="https://code.nightingale-ai.com/…"
              aria-invalid={urlInvalid || undefined}
              aria-describedby={urlInvalid ? 'automation-target-url-error' : undefined}
              onChange={event => setUrl(event.target.value)}
            />
            {urlInvalid && (
              <p id="automation-target-url-error" className="text-xs text-destructive">
                Use an https:// URL.
              </p>
            )}
          </div>
          <div className="space-y-2">
            <Label htmlFor="automation-target-secret">Signing secret</Label>
            <Input
              id="automation-target-secret"
              type="password"
              autoComplete="new-password"
              value={secret}
              maxLength={512}
              disabled={!encryptionAvailable}
              placeholder={target ? `Empty keeps ${target.secretHint}` : undefined}
              aria-describedby="automation-target-secret-hint"
              onChange={event => setSecret(event.target.value)}
            />
            <p id="automation-target-secret-hint" className="text-xs text-muted-foreground">
              {encryptionAvailable ? 'Shown only masked after saving.' : NO_ENCRYPTION}
            </p>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose} disabled={pending}>
              Cancel
            </Button>
            <Button type="submit" disabled={!ready || pending}>
              {pending ? 'Saving…' : 'Save'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function SettingsForm({ config }: { config: FeedbackAutomationConfig }) {
  const save = useSaveFeedbackAutomationSettings();
  const reset = useResetFeedbackAutomationSettings();
  const [enabled, setEnabled] = useState(config.enabled);
  const [threshold, setThreshold] = useState(String(config.minConfidence));
  const [categories, setCategories] = useState<string[]>(config.categories);
  const minConfidence = parseThreshold(threshold);
  const busy = save.isPending || reset.isPending;
  const changed =
    enabled !== config.enabled ||
    minConfidence !== config.minConfidence ||
    categories.length !== config.categories.length ||
    categories.some(category => !config.categories.includes(category));

  const toggle = (category: string, on: boolean) =>
    setCategories(current =>
      on ? [...current, category] : current.filter(item => item !== category)
    );

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (minConfidence === null || !changed || busy) return;
    try {
      await save.mutateAsync({
        enabled,
        minConfidence,
        categories,
        expectedRevision: config.revision,
      });
      toast.success('Settings saved');
    } catch (error) {
      toast.error(automationWriteErrorText(error, 'settings'));
    }
  };

  const resetToEnvironment = async () => {
    try {
      await reset.mutateAsync(undefined);
      toast.success('Settings reset to the environment');
    } catch (error) {
      toast.error(automationWriteErrorText(error, 'reset'));
    }
  };

  return (
    <section aria-label="Which tickets go" className="space-y-3 rounded-3xl border p-4">
      <div className="flex flex-wrap items-center gap-2">
        <h2 className="text-sm font-medium">Which tickets go</h2>
        <Badge variant="outline">
          {config.settingsSource === 'cortex' ? 'Set in Cortex' : 'From the environment'}
        </Badge>
      </div>
      <p className="text-xs text-muted-foreground">
        Tickets go to {destinationText(config)} when sending is on, their confidence reaches the
        threshold and their category is ticked.
      </p>
      <form className="space-y-4" onSubmit={event => void submit(event)}>
        <label className="flex items-center gap-3 text-sm">
          <Switch
            aria-label="Send confident tickets to Code automatically"
            checked={enabled}
            disabled={busy}
            onCheckedChange={setEnabled}
          />
          Send confident tickets to Code automatically
        </label>
        <div className="space-y-1">
          <Label htmlFor="automation-threshold">Minimum confidence, 0 to 100</Label>
          <Input
            id="automation-threshold"
            type="number"
            inputMode="numeric"
            min={0}
            max={100}
            step={1}
            className="w-28"
            value={threshold}
            disabled={busy}
            aria-invalid={minConfidence === null || undefined}
            onChange={event => setThreshold(event.target.value)}
          />
          {minConfidence === null && (
            <p className="text-xs text-destructive">Enter a whole number from 0 to 100.</p>
          )}
        </div>
        <fieldset className="space-y-2">
          <legend className="text-sm font-medium">Categories</legend>
          <div className="flex flex-wrap gap-x-6 gap-y-2">
            {categoryChoices(config.categories, config.availableCategories).map(category => (
              <label key={category} className="flex items-center gap-2 text-sm">
                <Checkbox
                  checked={categories.includes(category)}
                  disabled={busy}
                  onCheckedChange={checked => toggle(category, checked === true)}
                />
                {categoryLabel(category)}
              </label>
            ))}
          </div>
        </fieldset>
        <div className="flex flex-wrap gap-2">
          <Button type="submit" disabled={!changed || minConfidence === null || busy}>
            {save.isPending ? 'Saving…' : 'Save'}
          </Button>
          <Button
            type="button"
            variant="outline"
            disabled={config.settingsSource === 'environment' || busy}
            title={
              config.settingsSource === 'environment'
                ? 'These settings already come from the environment'
                : undefined
            }
            onClick={() => void resetToEnvironment()}
          >
            {reset.isPending ? 'Resetting…' : 'Reset to environment'}
          </Button>
        </div>
      </form>
    </section>
  );
}
